/**
 * Google Consent Mode v2 helpers.
 * Defaults are declared in public/index.html BEFORE any Google tag loads.
 * This module only reads/persists the user choice and pushes consent updates.
 */

export const CONSENT_STORAGE_KEY = "dbdg_consent_v1";
export const CONSENT_VERSION = 1;

export const DENIED_PREFS = { necessary: true, analytics: false, ads: false };
export const GRANTED_PREFS = { necessary: true, analytics: true, ads: true };

function hasWindow() {
  return typeof window !== "undefined";
}

/** Safe gtag wrapper: pushes to the same dataLayer created in index.html. */
export function gtag() {
  if (!hasWindow()) return;
  window.dataLayer = window.dataLayer || [];
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer.push(arguments);
}

export function toConsentSignals(prefs) {
  const ads = prefs && prefs.ads ? "granted" : "denied";
  const analytics = prefs && prefs.analytics ? "granted" : "denied";
  return {
    ad_storage: ads,
    ad_user_data: ads,
    ad_personalization: ads,
    analytics_storage: analytics,
    functionality_storage: "granted",
    personalization_storage: ads,
    security_storage: "granted",
  };
}

/** Reusable helper required by the spec. */
export function updateConsent(prefs) {
  if (!hasWindow()) return;
  const signals = toConsentSignals(prefs);
  gtag("consent", "update", signals);
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: "cookie_consent_update",
    consent_analytics: signals.analytics_storage,
    consent_ads: signals.ad_storage,
  });
}

export function readStoredConsent() {
  if (!hasWindow()) return null;
  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return {
      necessary: true,
      analytics: Boolean(parsed.analytics),
      ads: Boolean(parsed.ads),
      decidedAt: parsed.decidedAt || null,
      version: parsed.version || CONSENT_VERSION,
    };
  } catch (e) {
    return null;
  }
}

export function persistConsent(prefs) {
  if (!hasWindow()) return;
  try {
    window.localStorage.setItem(
      CONSENT_STORAGE_KEY,
      JSON.stringify({
        version: CONSENT_VERSION,
        necessary: true,
        analytics: Boolean(prefs.analytics),
        ads: Boolean(prefs.ads),
        decidedAt: new Date().toISOString(),
      }),
    );
  } catch (e) {
    /* storage unavailable */
  }
}

/** Persist + push update in one call. */
export function saveConsent(prefs) {
  persistConsent(prefs);
  updateConsent(prefs);
}

/** Re-apply a previously saved choice on subsequent visits. */
export function reapplyStoredConsent() {
  const stored = readStoredConsent();
  if (!stored) return null;
  updateConsent(stored);
  return stored;
}
